import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Card, Chip, Container, Grid, IconButton, List, ListItem, Stack, SwipeableDrawer, TextField, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { LoadingButton } from '@mui/lab';
import { getAllAssets } from '../../../redux/slices/assetsRedux/assetsRedux';
import { toast } from 'react-toastify';
import { accessVerify, capitalize, safeJSONParse, utcMoment } from '../../../utils/common';
import { CASHFLOW_PAYMENT_STATUS, CASHFLOW_TYPES } from '../../../utils/constants';
import { getPurchaseNotes } from '../../../redux/slices/purchaseRedux/purchaseNotesRedux';
import { addCashFlows, getCashFlows, updateCashFlows } from '../../../redux/slices/financeRedux/cashFlowRedux';
import { fCurrency } from '../../../utils/formatNumber';
import { getPaymentTypes } from '../../../redux/slices/financeRedux/paymentTypeRedux';
import UploadMultiFile from '../../../components/upload/UploadMultiFile';
import { imageUpload } from '../../../inteceptor';
import ImageViewer from 'react-simple-image-viewer';
import { AnimatePresence, m } from 'framer-motion';
import Image from '../../../components/Image';
import { alpha } from '@mui/material/styles';

import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import { v4 as uuidv4 } from 'uuid';
import PermissionRequired from '../../errorPages/permissionRequired';

export default function CashflowTransfers() {
  const { themeStretch } = useSettings();

  let locationList;
  let selectedLocation;
  ({ data: locationList, selectedLocation } = useSelector((state) => state.location));
  const [uuid, setUuid] = useState(uuidv4());
  const dataModel = {
    id: null,
    isDebit: null,
    dateTime: null,
    paymentDate: null,
    paidDate: null,
    title: '',
    description: '',
    type: 4,
    paymentTypeId: 1,
    status: 3,
    amount: 0,
    purchaseNoteId: null,
    posCashDrawersID: null,
    assetId: null,
    userId: null,
    locationId: selectedLocation?.id,
    linkedDocuments: [],
    isTransfer: 1,
    transferId: uuid,
    cashflowCategoriesId: null,
  };
  const [fromPaymentData, setFromPaymentData] = useState(dataModel);
  const [toPaymentData, setToPaymentData] = useState(dataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [cashflowList, setCashflowList] = useState([]);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [documentGallery, setDocumentGallery] = useState([]);
  const [paymentTypesList, setPaymentTypesList] = useState([]);

  const { data, addData } = useSelector((state) => state.cashFlow);
  const { loggedInUser } = useSelector((state) => state?.user);

  let tempPaymentTypeList;
  ({ data: tempPaymentTypeList } = useSelector((state) => state.paymentTypes));

  useEffect(() => {
    setPaymentTypesList(tempPaymentTypeList?.filter((value) => value?.isCashFlowTransferOption === 1));
  }, [tempPaymentTypeList]);

  useEffect(() => {
    dispatch(getCashFlows(selectedLocation?.id, true));
  }, [selectedLocation]);

  useEffect(() => {
    dispatch(getPaymentTypes());
    dispatch(getAllAssets());
    dispatch(getPurchaseNotes());
  }, []);

  useEffect(() => {
    setCashflowList(
      data?.map((value) => {
        return {
          ...value,
          linkedInfo: safeJSONParse(value?.linkedInfo),
          originalStatus: value?.status,
          linkedDocuments: safeJSONParse(value?.linkedInfo)?.cashFlowDocuments?.filter((value) => value?.url) || [],
          cashFlowLog: safeJSONParse(value?.linkedInfo)?.cashFlowLog?.filter((value) => value?.title) || [],
        };
      })
    );
    setIsModelOpen(false);
  }, [data]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsViewerOpen(false);
      setIsModelOpen(false);
      setFromPaymentData(dataModel);
      setToPaymentData({ ...dataModel, status: 1 });
      setDocumentGallery([]);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      setIsViewerOpen(false);
      if (type === 'add') {
        setUuid(uuidv4());
        setDocumentGallery([]);
        setFromPaymentData(dataModel);
        setToPaymentData({ ...dataModel, status: 1 });
      } else {
        setFromPaymentData(modelData);
        setToPaymentData({ ...modelData, status: 1 });
      }
    }
  };

  useEffect(() => {
    if (addData?.data) {
      dispatch(getCashFlows(selectedLocation?.id, true));
    }
  }, [addData]);

  useEffect(() => {
    setDocumentGallery(fromPaymentData?.linkedDocuments?.map((value) => value?.url));
  }, [fromPaymentData]);

  const updateFromPaymentData = (key, value) => {
    setFromPaymentData({
      ...fromPaymentData,
      [key]: value,
    });
  };
  const updateToPaymentData = (key, value) => {
    setToPaymentData({
      ...toPaymentData,
      [key]: value,
    });
  };

  const columns = !cashflowList?.[0]
    ? []
    : Object.keys(cashflowList?.[0])
        .map((value) => {
          if (['linkedInfo', 'linkedDocuments', 'cashFlowLog'].includes(value)) {
            return {};
          } else if (['isDebit'].includes(value)) {
            return {
              accessorKey: 'isDebit',
              header: 'Debit/Credit',
              Cell: ({ cell, row }) => {
                const rowValue = row?.original?.isDebit;
                return <Chip key={`${row?.original?.id}_${row?.index}`} label={rowValue ? 'Debit' : 'Credit'} color={rowValue ? 'success' : 'error'} />;
              },
            };
          } else if (['dateTime', 'paymentDate', 'paidDate'].includes(value)) {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                const rowValue = row?.original?.[value];
                const isValidDate = utcMoment(rowValue).isValid();
                return isValidDate ? utcMoment(rowValue).format('YYYY-MM-DD HH:mm:ss') : 'Not Paid Yet';
              },
            };
          } else if (['status'].includes(value)) {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                const rowValue = row?.original?.[value];
                const status = CASHFLOW_PAYMENT_STATUS?.find((value) => value?.id === rowValue)?.name;
                return <Chip key={`${row?.original?.id}_${row?.index}`} label={status} color={['Paid'].includes(status) ? 'success' : ['Pending'].includes(status) ? 'warning' : 'error'} />;
              },
            };
          } else if (['userId'].includes(value)) {
            return {
              accessorKey: value,
              header: 'Added User',
              Cell: ({ cell, row }) => {
                return row?.original?.linkedInfo?.userData?.name;
              },
            };
          } else if (['type'].includes(value)) {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return row?.original?.type ? CASHFLOW_TYPES?.find((value) => value?.id === row?.original?.type)?.name : 'Not Found';
              },
            };
          } else if (['assetId'].includes(value)) {
            return {
              accessorKey: value,
              header: 'Asset',
              Cell: ({ cell, row }) => {
                return row?.original?.linkedInfo?.asset?.id ? `${row?.original?.linkedInfo?.asset?.id} - ${row?.original?.linkedInfo?.asset?.name}` : 'Not Found';
              },
            };
          } else if (['amount'].includes(value)) {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return fCurrency(row?.original?.[value]);
              },
            };
          } else if (['purchaseNoteId'].includes(value)) {
            return {
              accessorKey: value,
              header: 'Purchase Note Id',
              Cell: ({ cell, row }) => {
                return row?.original?.purchaseNoteId ? `${row?.original?.purchaseNoteId}` : 'Not Found';
              },
            };
          } else if (['locationId'].includes(value)) {
            return {
              accessorKey: value,
              header: 'Location',
              Cell: ({ cell, row }) => {
                return row?.original?.linkedInfo?.locationData?.name ? `${row?.original?.linkedInfo?.locationData?.name}` : 'Not Found';
              },
            };
          } else if (['paymentTypeId'].includes(value)) {
            return {
              accessorKey: value,
              header: 'Transactional mediums',
              Cell: ({ cell, row }) => {
                return row?.original?.linkedInfo?.cashFlowPaymentType?.name ? `${row?.original?.linkedInfo?.cashFlowPaymentType?.name}` : 'Not Found';
              },
            };
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        ?.filter((value) => value?.accessorKey);

  const [rowSelection, setRowSelection] = useState(false);

  const handleSubmit = () => {
    if (fromPaymentData.title?.length === 0) {
      toast.error('Title is required!');
    } else if (fromPaymentData.description?.length === 0) {
      toast.error('Description is required!');
    } else if (fromPaymentData.amount === 0) {
      toast.error('Amount is required!');
    } else if (fromPaymentData.type === null) {
      toast.error('Type is required!');
    } else if (files?.length > 0) {
      toast.error('Related documents needs to be uploaded first!');
    } else {
      dispatch(isAdd ? addCashFlows({ ...fromPaymentData, type: 5 }) : updateCashFlows({ ...fromPaymentData, type: 5 }));
      dispatch(isAdd ? addCashFlows({ ...toPaymentData, type: 1 }) : updateCashFlows({ ...toPaymentData, type: 1 }));
    }
  };

  const uploadFiles = (file) => {
    return new Promise((resolve, reject) => {
      imageUpload(
        file,
        `Finance/Cashflow/Loc_${selectedLocation?.id}/User_${loggedInUser?.id}`,
        (data) => {
          if (data?.status) {
            resolve(data);
          } else {
            reject(new Error('Upload failed'));
          }
        },
        (errorMessage) => {
          console.error('Upload failed:', errorMessage);
          reject(new Error(errorMessage));
        }
      );
    });
  };

  const handleImageUpload = async () => {
    setIsUploading(true);
    for (const file of files) {
      try {
        setFiles((value) => {
          return value.map((fileValue, index) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: true,
              };
            }
            return fileValue;
          });
        });

        const data = await uploadFiles(file);

        setFiles((value) => {
          return value.filter((fileValue, index) => file?.id !== fileValue?.id);
        });
        if (data?.status) {
          setFromPaymentData((prevState) => ({
            ...prevState,
            linkedDocuments: [...prevState.linkedDocuments, { name: file?.name, url: data?.url }],
          }));
          setToPaymentData((prevState) => ({
            ...prevState,
            linkedDocuments: [...prevState.linkedDocuments, { name: file?.name, url: data?.url }],
          }));
        }
      } catch (error) {
        console.error(error);
        setFiles((value) => {
          return value.map((fileValue, index) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: false,
              };
            }
            return fileValue;
          });
        });
      }
    }
    setIsUploading(false);
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  return (
    <Page title={`Cashflow Transfers`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Cashflow Transfers`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Finance',
              href: '',
            },
            { name: `Cashflow Transfers` },
          ]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => {
                manageModel(dataModel, 'add');
              }}
            >
              New Record
            </Button>
          }
        />
        {accessVerify('CASHFLOW_TRANSFERS_VIEW') ? (
          <DataGridTable
            name={'Assets List'}
            data={cashflowList}
            column={[...columns]}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={false}
            isRowClickable={true}
            onRowClick={(row) => {
              manageModel(row?.original, 'edit');
            }}
            renderRowActionItems={(value, closeMenu) => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor={'bottom'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '95%',
              height: '95%',
              marginLeft: '2.5%',
              borderRadius: '10px',
            },
          }}
        >
          <Grid container spacing={1} padding={2}>
            <Grid item xs={12} md={8}>
              <Box sx={{ p: 2 }}>
                <h1>{isAdd ? 'Add' : 'Edit'} Record</h1>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                color='info'
                variant='outlined'
                size='large'
                style={{ width: '100%' }}
                onClick={() => {
                  manageModel(dataModel, 'add');
                }}
              >
                Close
              </Button>
              <LoadingButton type='submit' variant='contained' size='large' style={{ width: '100%', marginLeft: '8px' }} onClick={handleSubmit}>
                {isAdd ? 'Add Record' : 'Save Changes'}
              </LoadingButton>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ p: 3 }}>
                <Typography variant='subtitle1' padding='10px 0'>
                  From Payment Information
                </Typography>
                <Stack spacing={3} sx={{ p: 3 }}>
                  <Card sx={{ p: 3 }}>
                    <Stack spacing={3}>
                      <Autocomplete
                        onChange={(event, newValue) => {
                          if (isAdd) {
                            updateFromPaymentData('paymentTypeId', paymentTypesList.find((value) => value?.id === newValue?.value)?.id);
                          }
                        }}
                        options={paymentTypesList?.map((value) => ({
                          label: `${value?.name}`,
                          value: value?.id,
                        }))}
                        value={
                          paymentTypesList?.find((value) => value.id === fromPaymentData?.paymentTypeId)?.name && {
                            label: `${paymentTypesList?.find((value) => value.id === fromPaymentData?.paymentTypeId)?.name || 'Transactional mediums'}`,
                            value: fromPaymentData?.type,
                          }
                        }
                        renderInput={(params) => <TextField label='Select Transactional mediums' {...params} />}
                        disabled={!isAdd}
                      />

                      <TextField fullWidth label='Title' value={fromPaymentData?.title} onChange={(e) => updateFromPaymentData('title', e.target.value)} />

                      <TextField fullWidth label='Description' value={fromPaymentData?.description} onChange={(e) => updateFromPaymentData('description', e.target.value)} />

                      <TextField
                        fullWidth
                        type='number'
                        label='Amount'
                        value={fromPaymentData?.amount}
                        onChange={(e) => {
                          if (isAdd) {
                            updateFromPaymentData('amount', e.target.value);
                            updateToPaymentData('amount', e.target.value);
                          }
                        }}
                        disabled={!isAdd}
                      />
                    </Stack>
                  </Card>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card sx={{ p: 3 }}>
                <Typography variant='subtitle1' padding='10px 0'>
                  Related Documents
                </Typography>
                <UploadMultiFile
                  error={false}
                  showPreview={true}
                  files={files}
                  onRemove={(removedFile) => {
                    setFiles(files.filter((file) => file !== removedFile));
                  }}
                  onRemoveAll={() => {
                    setFiles([]);
                  }}
                  helperText={''}
                  onDrop={(uploadedFiles) => {
                    setFiles((value) => [
                      ...value,
                      ...uploadedFiles.map((file, index) =>
                        Object.assign(file, {
                          id: index,
                          preview: URL.createObjectURL(file),
                          isUploading: false,
                        })
                      ),
                    ]);
                  }}
                  onUpload={() => {
                    handleImageUpload();
                  }}
                  isUploading={isUploading}
                />

                {documentGallery?.length > 0 && (
                  <Typography variant='subtitle1' padding='10px 0'>
                    Uploaded Images
                  </Typography>
                )}
                <List disablePadding sx={{ my: 3 }}>
                  <AnimatePresence>
                    {documentGallery
                      ?.filter((value) => value)
                      .map((src, index) => (
                        <>
                          <ListItem
                            key={index}
                            component={m.div}
                            onClick={() => {
                              openImageViewer(index);
                            }}
                            sx={{
                              p: 0,
                              m: 0.5,
                              width: 80,
                              height: 80,
                              borderRadius: 1.25,
                              overflow: 'hidden',
                              position: 'relative',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              border: (theme) => `solid 1px ${theme.palette.divider}`,
                            }}
                          >
                            <Image alt='preview' src={src} ratio='1/1' />
                            <IconButton
                              size='small'
                              sx={{
                                top: 6,
                                p: '2px',
                                right: 6,
                                position: 'absolute',
                                color: 'common.white',
                                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                                '&:hover': {
                                  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                                },
                              }}
                            ></IconButton>
                          </ListItem>
                        </>
                      ))}
                  </AnimatePresence>
                </List>
              </Card>
            </Grid>

            {isAdd && (
              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    To Payment Information
                  </Typography>
                  <Stack spacing={3} sx={{ p: 3 }}>
                    <Card sx={{ p: 3 }}>
                      <Stack spacing={3}>
                        <Autocomplete
                          onChange={(event, newValue) => {
                            if (isAdd) {
                              updateToPaymentData('locationId', locationList.find((value) => value?.id === newValue?.value)?.id);
                            }
                          }}
                          options={locationList?.map((value) => ({
                            label: `${value?.name}`,
                            value: value?.id,
                          }))}
                          value={
                            locationList?.find((value) => value.id === toPaymentData?.locationId)?.name && {
                              label: `${locationList?.find((value) => value.id === toPaymentData?.locationId)?.name || 'Location name'}`,
                              value: toPaymentData?.type,
                            }
                          }
                          renderInput={(params) => <TextField label='Location Name' {...params} />}
                          disabled={!isAdd}
                        />

                        <Autocomplete
                          onChange={(event, newValue) => {
                            if (isAdd) {
                              updateToPaymentData('paymentTypeId', paymentTypesList.find((value) => value?.id === newValue?.value)?.id);
                            }
                          }}
                          options={paymentTypesList?.map((value) => ({
                            label: `${value?.name}`,
                            value: value?.id,
                          }))}
                          value={
                            paymentTypesList?.find((value) => value.id === toPaymentData?.paymentTypeId)?.name && {
                              label: `${paymentTypesList?.find((value) => value.id === toPaymentData?.paymentTypeId)?.name || 'Transactional mediums'}`,
                              value: toPaymentData?.type,
                            }
                          }
                          renderInput={(params) => <TextField label='Select Transactional mediums' {...params} />}
                          disabled={!isAdd}
                        />

                        <TextField fullWidth label='Title' value={toPaymentData?.title} onChange={(e) => updateToPaymentData('title', e.target.value)} />

                        <TextField fullWidth label='Description' value={toPaymentData?.description} onChange={(e) => updateToPaymentData('description', e.target.value)} />

                        <TextField fullWidth type='number' label='Amount' value={toPaymentData?.amount} onChange={(e) => {}} disabled={true} />
                      </Stack>
                    </Card>
                  </Stack>
                </Card>
              </Grid>
            )}

            {fromPaymentData?.cashFlowLog?.length > 0 && (
              <Grid item xs={12} md={6} lg={4}>
                <Card>
                  <Typography variant='subtitle1' sx={{ p: 3 }}>
                    History Information
                  </Typography>
                  <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Timeline position='alternate'>
                      {fromPaymentData?.cashFlowLog?.map((value) => (
                        <TimelineItem key={value.id}>
                          <TimelineOppositeContent sx={{ m: 'auto 0' }} align='right' variant='body2' color='text.secondary'>
                            {utcMoment(value?.dateTime).format('YYYY-MM-DD HH:mm:ss')}
                          </TimelineOppositeContent>
                          <TimelineSeparator>
                            <TimelineConnector />
                            <TimelineDot>
                              <Iconify icon='eva:checkmark-circle-2-fill' />
                            </TimelineDot>
                            <TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent sx={{ py: '12px', px: 2 }}>
                            <Typography variant='body1' component='span'>
                              <b>{value?.title}</b>
                            </Typography>
                            <Typography>{value?.description}</Typography>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </Timeline>
                  </div>
                </Card>
              </Grid>
            )}
          </Grid>
          {isViewerOpen && <ImageViewer src={documentGallery} currentIndex={currentImage} disableScroll={false} closeOnClickOutside={true} onClose={closeImageViewer} />}
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
